import "core-js/modules/es.array.push.js";
import { computed, defineComponent, reactive, ref } from "vue";
import { DeleteOutlined } from "@ant-design/icons-vue";
const createCondition = (id, field, operator, value) => ({
  id,
  field,
  operator,
  value
});
export default defineComponent({
  name: "StoreLocationFilterPanel",
  emits: ["apply-group", "validation-failed", "save-template", "select-template", "tab-change"],
  props: {
    savedRules: {
      type: Array,
      default: () => []
    },
    conditionOptions: {
      type: Object,
      default: () => ({})
    },
    cityCascaderOptions: {
      type: Array,
      default: () => []
    }
  },
  components: {
    DeleteOutlined
  },
  setup(props, {
    expose,
    emit
  }) {
    const validationMessage = "请先完善筛选规则";
    const fieldOptions = [{
      value: "Store Name",
      label: "店铺名称"
    }, {
      value: "City",
      label: "省份及城市"
    }, {
      value: "Door Concept",
      label: "Door Concept"
    }, {
      value: "Support Type",
      label: "支持类型"
    }, {
      value: "AEC",
      label: "AEC"
    }, {
      value: "Gender",
      label: "性别"
    }, {
      value: "Category",
      label: "Category"
    }, {
      value: "Region",
      label: "区域"
    }, {
      value: "Location",
      label: "位置"
    }, {
      value: "Location Summary",
      label: "位置描述"
    }];
    const operatorOptions = [{
      value: "contains",
      label: "包含"
    }];
    const textFields = ["Store Name"];
    const cityFields = ["City"];
    const fieldAliases = {
      ACE: "AEC",
      "SupportType": "Support Type",
      supportType: "Support Type",
      location: "Location",
      locationSummary: "Location Summary"
    };
    const groups = reactive([]);
    const nextGroupId = ref(1);
    const nextConditionId = ref(1);
    const activeGroupKey = ref("");
    const getGroupSelectedFields = (groupId, excludedConditionId = null) => {
      const targetGroup = groups.find(group => group.id === groupId);
      if (!targetGroup) {
        return [];
      }
      return targetGroup.conditions.filter(condition => condition.id !== excludedConditionId).map(condition => condition.field).filter(Boolean);
    };
    const getNextAvailableField = (groupId, excludedConditionId = null) => {
      const selectedFields = getGroupSelectedFields(groupId, excludedConditionId);
      const nextField = fieldOptions.find(option => !selectedFields.includes(option.value));
      return nextField ? nextField.value : fieldOptions[0]?.value || null;
    };
    const buildCondition = groupId => {
      const nextField = getNextAvailableField(groupId);
      const condition = createCondition(`condition-${nextConditionId.value}`, nextField, "contains", nextField && isTextField(nextField) ? "" : []);
      nextConditionId.value += 1;
      return condition;
    };
    const createGroup = groupId => ({
      id: `group-${groupId}`,
      conditions: [buildCondition(groupId)]
    });
    const showNextGroup = () => {
      const nextGroup = createGroup(nextGroupId.value);
      groups.push(nextGroup);
      activeGroupKey.value = String(nextGroup.id);
      nextGroupId.value += 1;
      emit("tab-change", buildGroupPayload(nextGroup));
    };
    const addCondition = groupId => {
      const group = groups.find(item => item.id === groupId);
      if (!group) {
        return;
      }
      if (!hasAvailableFields(groupId)) {
        return;
      }
      group.conditions.push(buildCondition(groupId));
    };
    const removeGroup = groupId => {
      const targetIndex = groups.findIndex(item => item.id === groupId);
      if (targetIndex !== -1) {
        groups.splice(targetIndex, 1);
        if (groups.length === 0) {
          activeGroupKey.value = "";
          emit("tab-change", null);
          return;
        }
        const nextActiveGroup = groups[targetIndex] || groups[targetIndex - 1];
        activeGroupKey.value = String(nextActiveGroup.id);
        emit("tab-change", buildGroupPayload(nextActiveGroup));
      }
    };
    const removeCondition = (groupId, conditionId) => {
      const group = groups.find(item => item.id === groupId);
      if (!group || group.conditions.length <= 1) {
        return;
      }
      const targetIndex = group.conditions.findIndex(item => item.id === conditionId);
      if (targetIndex !== -1) {
        group.conditions.splice(targetIndex, 1);
      }
    };
    const isTextField = field => textFields.includes(field);
    const isCityField = field => cityFields.includes(field);
    const normalizeOption = item => {
      if (typeof item === "string" || typeof item === "number") {
        return {
          label: String(item),
          value: item
        };
      }
      if (item && typeof item === "object") {
        const rawLabel = typeof item.label === "object" ? item.label.city || item.label.name || item.label.value || "" : item.label;
        const rawText = typeof item.text === "object" ? item.text.city || item.text.name || item.text.value || "" : item.text;
        const rawValue = typeof item.value === "object" ? item.value.city || item.value.name || item.value.value || "" : item.value;
        return {
          label: rawLabel ?? rawText ?? item.name ?? rawValue ?? "",
          value: rawValue ?? rawLabel ?? rawText ?? item.name ?? ""
        };
      }
      return {
        label: "",
        value: ""
      };
    };
    const getFieldValueOptions = field => {
      const rawOptions = props.conditionOptions?.[field] || [];
      return rawOptions.map(normalizeOption).filter(item => item.label !== "" && item.value !== "");
    };
    const filterSelectOption = (inputValue, option) => {
      const label = String(option?.label || "").toLowerCase();
      return label.includes(String(inputValue || "").toLowerCase());
    };
    const getPopupContainer = triggerNode => triggerNode?.closest?.(".filter-panel") || triggerNode?.parentNode || document.body;
    const onFieldChange = (condition, nextField) => {
      condition.field = nextField;
      condition.value = isTextField(nextField) ? "" : [];
    };
    const getAvailableFieldOptions = (groupId, conditionId) => {
      const selectedFields = getGroupSelectedFields(groupId, conditionId);
      return fieldOptions.filter(option => !selectedFields.includes(option.value));
    };
    const hasAvailableFields = (groupId = null) => {
      if (groupId == null) {
        return fieldOptions.length > 0;
      }
      return getAvailableFieldOptions(groupId, null).length > 0;
    };
    const displaySavedRules = computed(() => (props.savedRules || []).filter(item => {
      if (typeof item === "string") {
        return false;
      }
      return !!(item?.templateName || item?.name || item?.title);
    }));
    const getProvinceCities = provinceValue => {
      const provinceOption = (props.cityCascaderOptions || []).find(item => item?.value === provinceValue);
      if (!provinceOption || !Array.isArray(provinceOption.children)) {
        return [];
      }
      return provinceOption.children.map(city => city?.value).filter(city => city !== null && city !== undefined && city !== "");
    };
    const normalizeConditionValues = condition => {
      if (isTextField(condition.field)) {
        const textValue = typeof condition.value === "string" ? condition.value.trim() : "";
        return textValue ? [textValue] : [];
      }
      if (isCityField(condition.field)) {
        if (!Array.isArray(condition.value)) {
          return [];
        }
        return [...new Set(condition.value.flatMap(item => {
          if (Array.isArray(item) && item.length > 0) {
            if (item.length === 1) {
              return getProvinceCities(item[0]);
            }
            return item[item.length - 1];
          }
          if (typeof item === "string" || typeof item === "number") {
            return getProvinceCities(item);
          }
          return [];
        }))].filter(item => item !== null && item !== undefined && item !== "");
      }
      if (!Array.isArray(condition.value)) {
        return [];
      }
      return condition.value.filter(item => item !== null && item !== undefined && item !== "");
    };
    const getGroupDisplayNumber = groupId => {
      const groupIndex = groups.findIndex(group => group.id === groupId);
      return groupIndex === -1 ? 0 : groupIndex + 1;
    };
    const buildGroupPayload = group => ({
      groupName: `GROUP ${getGroupDisplayNumber(group.id)}`,
      rules: group.conditions.map(condition => ({
        field: condition.field,
        operator: condition.operator,
        values: normalizeConditionValues(condition)
      }))
    });
    const getExportPayload = () => ({
      groups: groups.map(group => ({
        groupName: `GROUP ${getGroupDisplayNumber(group.id)}`,
        rules: group.conditions.map(condition => ({
          field: condition.field,
          operator: condition.operator,
          values: normalizeConditionValues(condition)
        }))
      }))
    });
    const cloneConditionValue = condition => {
      if (isTextField(condition.field)) {
        return typeof condition.value === "string" && condition.value !== "" ? [condition.value] : [];
      }
      if (!Array.isArray(condition.value)) {
        return [];
      }
      return condition.value.map(item => {
        if (Array.isArray(item)) {
          return [...item];
        }
        return item;
      });
    };
    const getTemplatePayload = () => ({
      groups: groups.map(group => ({
        groupName: `GROUP ${getGroupDisplayNumber(group.id)}`,
        rules: group.conditions.map(condition => ({
          field: condition.field,
          operator: condition.operator,
          values: cloneConditionValue(condition)
        }))
      }))
    });
    const isConditionComplete = condition => {
      if (!condition.field || !condition.operator) {
        return false;
      }
      return normalizeConditionValues(condition).length > 0;
    };
    const validateGroup = group => !!group && Array.isArray(group.conditions) && group.conditions.length > 0 && group.conditions.every(isConditionComplete);
    const validateAllGroups = () => groups.length > 0 && groups.every(validateGroup);
    const applyGroup = group => {
      if (!validateGroup(group)) {
        emit("validation-failed", validationMessage);
        return;
      }
      emit("apply-group", buildGroupPayload(group));
    };
    const normalizeTemplateRuleValue = (field, values) => {
      if (isTextField(field)) {
        const firstValue = Array.isArray(values) && values.length > 0 ? values[0] : "";
        return typeof firstValue === "string" ? firstValue : "";
      }
      if (!Array.isArray(values)) {
        return [];
      }
      if (isCityField(field)) {
        return values.map(item => Array.isArray(item) ? [...item] : []).filter(item => item.length > 0);
      }
      return values.filter(item => item !== null && item !== undefined && item !== "");
    };
    const setGroups = templateGroups => {
      groups.splice(0, groups.length);
      nextGroupId.value = 1;
      nextConditionId.value = 1;
      if (!Array.isArray(templateGroups) || templateGroups.length === 0) {
        return;
      }
      templateGroups.forEach((group, groupIndex) => {
        const nextGroup = {
          id: `group-${nextGroupId.value}`,
          conditions: []
        };
        nextGroupId.value += 1;
        const rules = Array.isArray(group?.rules) ? group.rules : [];
        rules.forEach(rule => {
          const normalizedField = fieldAliases[rule?.field] || rule?.field;
          const field = fieldOptions.some(option => option.value === normalizedField) ? normalizedField : fieldOptions[0].value;
          nextGroup.conditions.push(createCondition(`condition-${nextConditionId.value}`, field, rule?.operator || "contains", normalizeTemplateRuleValue(field, rule?.values)));
          nextConditionId.value += 1;
        });
        if (nextGroup.conditions.length === 0) {
          nextGroup.conditions.push(buildCondition(nextGroup.id));
        }
        groups.push(nextGroup);
      });
      activeGroupKey.value = groups.length > 0 ? String(groups[0].id) : "";
      emit("tab-change", groups.length > 0 ? buildGroupPayload(groups[0]) : null);
    };
    const saveCurrentTemplate = () => {
      if (!validateAllGroups()) {
        emit("validation-failed", validationMessage);
        return;
      }
      emit("save-template", getTemplatePayload());
    };
    const selectSavedRule = rule => {
      emit("select-template", rule);
    };
    const handleTabChange = nextKey => {
      activeGroupKey.value = String(nextKey);
      const activeGroup = groups.find(group => String(group.id) === String(nextKey));
      emit("tab-change", activeGroup ? buildGroupPayload(activeGroup) : null);
    };
    expose({
      getExportPayload,
      validateAllGroups,
      getTemplatePayload,
      setGroups
    });
    return {
      displaySavedRules,
      fieldOptions,
      operatorOptions,
      groups,
      activeGroupKey,
      hasAvailableFields,
      isTextField,
      isCityField,
      getFieldValueOptions,
      filterSelectOption,
      getPopupContainer,
      getAvailableFieldOptions,
      buildGroupPayload,
      onFieldChange,
      validateAllGroups,
      showNextGroup,
      addCondition,
      removeGroup,
      removeCondition,
      applyGroup,
      saveCurrentTemplate,
      selectSavedRule,
      handleTabChange
    };
  }
});